<template>
    <switch-theme-message />

    <LaPageHeader heading="Dashboard" data-cy="ltba-dashboard">
        <template #controls>
            <change-device @device-change="deviceChange" />

            <!-- Websites -->
            <LaWebsiteDatePickers
                v-if="websites?.length"
                v-model="websiteDatesModel"
                :websites="websites"
                :tz="tz"
                :multiple="true"
                :select-all-defaults="true"
            />
        </template>
    </LaPageHeader>

    <portal-target name="dashboardTop" v-if="websites?.length" />

    <LaNoData
        v-if="!selectedPublisher"
        header="Select publisher"
        description="Please select a publisher to show LTBA data."
    />

    <LaNoData
        v-else-if="websites && !selectedWebsites?.length"
        header="Add website"
        description="Please add website to show block rate data for it."
    >
        <LaAddWebsite />
    </LaNoData>

    <template
        v-else-if="
            selectedPublisher &&
            websites &&
            selectedWebsites.length &&
            selectedWebsites.length > 0 &&
            selectedWebsites.length < 100 &&
            websitesReady
        "
    >
        <LaEarningsCards v-if="monetizing" :payload="filterData" :websites="selectedWebsites" />
        <LaBlockRateCards v-else :payload="filterData" :websites="selectedWebsites" />
    </template>
</template>

<script>
import LaPageHeader from "@/components/PageHeader";
import store from "@/store/store";
import LaNoData from "@/components/NoData";
import LaWebsiteDatePickers from "@/components/filters/WebsiteDatePickers";
import LaEarningsCards from "@/components/earnings/EarningsCards";
import LaBlockRateCards from "@/components/dashboard/BlockRateCards";
import ChangeDevice from "@/components/dashboard/ChangeDevice.vue";
import {
    getterTypes as websiteDatesGetterTypes,
    actionTypes as websiteDatesActionTypes,
} from "@/store/WebsiteDates/types";
import { getterTypes as websiteGetterTypes, actionTypes as websiteActionTypes } from "@/store/Websites/types";
import { getterTypes as userGetterTypes } from "@/store/LoggedUser/types";
import { getterTypes as publisherGetterTypes, actionTypes as publisherActionTypes } from "@/store/Publisher/types";
import { computed, watch, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { getValidAabSiteIds, isWebsiteMeasured, isWebsiteMonetized, getDefaultPeriod } from "@/helpers/data";
import { useDashboardState } from "@/composables/useDashboardState";
import LaAddWebsite from "@/components/button/AddWebsite";
import SwitchThemeMessage from "@/components/SwitchThemeMessage.vue";

import { actionTypes as blockRateActionTypes } from "@/store/BlockRate/types";

export default {
    name: "DashboardLtba",
    components: {
        SwitchThemeMessage,
        LaPageHeader,
        LaAddWebsite,
        LaBlockRateCards,
        LaWebsiteDatePickers,
        LaNoData,
        LaEarningsCards,
        ChangeDevice,
    },
    props: {
        from_database: {
            type: String,
            default: "sql",
        },
    },
    methods: {
        updateBlockRate() {
            store.dispatch(blockRateActionTypes.updateBlockRate);
        },
    },
    setup(props) {
        const router = useRouter();
        const route = useRoute();

        const loading = computed(() => !filterData.value || store.getters[websiteGetterTypes.isLoading]);
        const aabSiteIds = computed(() => store.getters[websiteDatesGetterTypes.getAabSiteIds]);
        const period = computed(() => store.getters[websiteDatesGetterTypes.getPeriod]);
        const userId = computed(() => store.getters[userGetterTypes.getUserId]);
        const tz = computed(() => store.getters[userGetterTypes.getTimezone]);
        const publisher = computed(() => store.getters[publisherGetterTypes.getPublisher]);
        const websitesReady = ref(false);

        // Use the dashboard state composable
        const {
            clearDashboardData,
            clearLocalStorage,
            loadAndSetWebsitesForPublisher,
            setupRouteWatcher,
            setupUnmountCleanup,
            handlePublisherChange,
            getStoredPeriod,
            namespace,
        } = useDashboardState("ltba", route);

        // Set selected mode
        store.dispatch(publisherActionTypes.setSelectedMode, "ltba");

        // LTBA-specific state
        const ltbaPublishers = computed(() => {
            const allPublishers = store.getters[publisherGetterTypes.getPublishers] || [];
            return allPublishers.filter((p) => !p.is_yield);
        });

        const selectedPublisher = computed(() => store.getters[publisherGetterTypes.getSelectedPublisherLtba]);
        const selectedWebsites = computed(() => {
            const websites = store.getters[publisherGetterTypes.getSelectedWebsitesLtba] || [];
            const currentPublisher = selectedPublisher.value;

            // Filter only websites that belong to current publisher
            if (currentPublisher && websites.length > 0) {
                const filtered = websites.filter((web) => web.publisher_id === currentPublisher.id);
                return filtered;
            }
            return [];
        });

        // Initialize publishers if not loaded
        if (!ltbaPublishers.value || ltbaPublishers.value.length === 0) {
            store.dispatch(publisherActionTypes.getData);
        }

        // Publisher model for v-model binding
        const selectedPublisherModel = computed({
            get: () => selectedPublisher.value,
            set: async (val) => {
                websitesReady.value = false;

                // ALWAYS clear old websites first to prevent contamination
                store.dispatch(publisherActionTypes.setSelectedWebsitesLtba, []);
                clearDashboardData();

                // Set new publisher
                store.dispatch(publisherActionTypes.setSelectedPublisherLtba, val);

                if (val) {
                    // Load and set websites using centralized function. Pass null period
                    // so the composable falls back to whatever is stored in LS — avoids
                    // clobbering the user's saved period with the default.
                    await loadAndSetWebsitesForPublisher(val.id, userId.value, period.value);
                    websitesReady.value = true;
                } else {
                    // No publisher selected, just clear
                    clearLocalStorage(userId.value, period.value);
                }
            },
        });

        // Full list of websites for the current publisher — this is the picker's
        // source of options. Selected subset lives separately in selectedWebsitesLtba.
        const websites = computed(() => {
            const allWebsites = store.getters[websiteGetterTypes.getWebsites];
            const currentPublisher = selectedPublisher.value;
            if (!allWebsites || !currentPublisher) return null;
            return allWebsites
                .filter((web) => web.publisher_id === currentPublisher.id)
                .map((web) => ({ ...web, disabled: !isWebsiteMeasured(web) }));
        });

        const monetizing = computed(() => selectedWebsites.value?.some(isWebsiteMonetized));

        const onChangeWebsiteDates = (val) => {
            if (!val.period || !val.websites || !userId.value) {
                return;
            }

            const validWebsiteIds = getValidAabSiteIds(val.websites);
            store.dispatch(websiteDatesActionTypes.setWebsiteDatesToLS, {
                aabSiteIds: validWebsiteIds,
                period: val.period,
                userId: userId.value,
                namespace: namespace,
            });
            store.dispatch(publisherActionTypes.setSelectedWebsitesLtba, val.websites);
        };

        const filterData = computed(() => {
            const data = {
                period: period.value,
                aabSiteIds: aabSiteIds.value,
                timezone: tz.value,
                source: props.from_database,
            };
            return data;
        });

        const websiteDatesModel = computed({
            get: () => ({
                websites: selectedWebsites.value,
                period: period.value,
            }),
            set: onChangeWebsiteDates,
        });

        const deviceChange = (device) => {
            let payload = { ...filterData.value, device };
            store.dispatch(blockRateActionTypes.getData, payload);
        };

        // Setup route watcher using composable
        setupRouteWatcher(async (oldName, newName) => {
            if (newName === "dashboard-ltba") {
                websitesReady.value = false;

                // If we're coming from a different dashboard, ensure we load correct websites
                if (oldName && oldName !== newName && selectedPublisher.value) {
                    const ready = await handlePublisherChange(selectedPublisher.value.id, userId.value, period.value);
                    websitesReady.value = ready;
                }
            }
        });

        // Restore from localStorage on mount
        store.dispatch(publisherActionTypes.restoreFromLS);

        // Synchronously populate WebsiteDates.period from LS so the UI shows the saved
        // period immediately, before any async load/watch can fall through to defaults.
        if (userId.value) {
            store.dispatch(websiteDatesActionTypes.getWebsiteDatesfromLS, {
                userId: userId.value,
                namespace: namespace,
            });
        }

        // Sync with URL params - watch route object for better reactivity
        watch(
            [route, ltbaPublishers],
            async ([currentRoute, publishers]) => {
                if (currentRoute && currentRoute.name === "dashboard-ltba" && publishers && publishers.length > 0) {
                    // Sync publisher from URL
                    if (currentRoute.params && currentRoute.params.publisher) {
                        const publisherId = parseInt(currentRoute.params.publisher);
                        const foundPublisher = publishers.find((p) => p.id === publisherId);
                        if (foundPublisher) {
                            // Always load websites when publisher is in URL, even if already selected
                            websitesReady.value = false;

                            // Clear old data — pass null period so the composable preserves
                            // the LS-stored period (Vuex period.value is reset on remount).
                            clearLocalStorage(userId.value, period.value);

                            // Set publisher if different
                            if (selectedPublisher.value?.id !== publisherId) {
                                store.dispatch(publisherActionTypes.setSelectedPublisherLtba, foundPublisher);
                            }

                            await loadAndSetWebsitesForPublisher(foundPublisher.id, userId.value, period.value);
                            websitesReady.value = true;
                        }
                    } else if ((!currentRoute.params || !currentRoute.params.publisher) && publishers.length > 0) {
                        // No publisher in URL — only use restored publisher if available.
                        // Otherwise let LtbaPublisherPicker handle the selection.
                        const restoredPublisher = selectedPublisher.value;
                        if (restoredPublisher && publishers.find((p) => p.id === restoredPublisher.id)) {
                            websitesReady.value = false;
                            await loadAndSetWebsitesForPublisher(restoredPublisher.id, userId.value, period.value);
                            websitesReady.value = true;
                        }
                    }

                    // Sync websites from URL
                    if (
                        currentRoute.params &&
                        currentRoute.params.webs &&
                        websites.value?.length &&
                        selectedPublisher.value
                    ) {
                        const websParam = Array.isArray(currentRoute.params.webs)
                            ? currentRoute.params.webs.join(",")
                            : currentRoute.params.webs;
                        const urlWebIds = websParam
                            .split(",")
                            .map((id) => parseInt(id.trim()))
                            .filter((id) => !isNaN(id));

                        if (urlWebIds.length > 0) {
                            const validWebsites = websites.value.filter((web) => urlWebIds.includes(web.aab_site_id));
                            if (validWebsites.length > 0) {
                                store.dispatch(publisherActionTypes.setSelectedWebsitesLtba, validWebsites);
                                store.dispatch(websiteDatesActionTypes.setWebsiteDatesToLS, {
                                    aabSiteIds: urlWebIds,
                                    period: period.value || getStoredPeriod(userId.value) || getDefaultPeriod(),
                                    userId: userId.value,
                                    namespace: namespace,
                                });
                            }
                        }
                    }
                }
            },
            { immediate: true }
        );

        // Update URL when selection changes
        watch([selectedPublisher, selectedWebsites], ([publisher, websites]) => {
            if (publisher && route && route.name === "dashboard-ltba") {
                const newParams = { publisher: publisher.id.toString() };
                if (websites && websites.length > 0) {
                    const websiteIds = websites.map((w) => w.aab_site_id).join(",");
                    newParams.webs = websiteIds;
                }
                router.replace({ name: "dashboard-ltba", params: newParams }).catch(() => {
                    // Ignore navigation duplicated error
                });
            }
        });

        // Load websites when missing and restore WebsiteDates from localStorage
        watch(
            [websites, userId, selectedPublisher],
            ([websitesValue, userIdValue, publisherValue]) => {
                // Only proceed if we're on the LTBA dashboard
                if (route.name !== "dashboard-ltba") {
                    return;
                }

                // Auto-load websites if missing but have publisher
                if (!websitesValue && publisherValue && userIdValue) {
                    store.dispatch(websiteActionTypes.getData);
                    return;
                }

                // Restore WebsiteDates period from localStorage if missing.
                if (userIdValue && !period.value) {
                    store.dispatch(websiteDatesActionTypes.getWebsiteDatesfromLS, {
                        userId: userIdValue,
                        namespace: namespace,
                    });
                }
            },
            { immediate: true }
        );

        // Setup cleanup on component unmount
        setupUnmountCleanup();

        return {
            loading,
            websites,
            websiteDatesModel,
            monetizing,
            filterData,
            selectedWebsites,
            selectedPublisher,
            ltbaPublishers,
            selectedPublisherModel,
            publisher,
            tz,
            deviceChange,
            websitesReady,
        };
    },
};
</script>

<style lang="scss" scoped>
select#available_slots {
    appearance: auto;
    padding: 5px 10px;
    width: auto;
    box-sizing: border-box;
}
</style>
